/* Sprint 1 (FLY2619) , External Web Intake Form.

Distinct, approachable UI. NOT the HR command center. This is the surface
employees, contractors, and third parties use to submit a case.

Routes:
  /intake/:tenantSlug          , the intake form (Named + Anonymous)
  /intake/status?case=PHR-...  , the case status portal (see IntakeStatus.jsx)
*/
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Lock, User as UserIcon, ArrowRight, CheckCircle2, Search, Copy,
  Info, HelpCircle, ShieldCheck, MessageSquare,
} from "lucide-react";
import Logo from "../components/Logo";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_LABELS = {
  low: "Not urgent",
  medium: "Regular",
  high: "Priority",
  critical: "Urgent, safety concern",
};

export default function Intake() {
  const { tenantSlug: slugParam } = useParams();
  const tenantSlug = (slugParam || "acme").toLowerCase();
  const [tenant, setTenant] = useState(null);
  const [allegations, setAllegations] = useState([]);
  const [channel, setChannel] = useState(null); // null | "named" | "anonymous"
  const [receipt, setReceipt] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_BASE}/intake/${tenantSlug}/allegations`)
      .then((r) => { setTenant(r.data.tenant); setAllegations(r.data.allegations); })
      .catch(() => toast.error("Workspace not found. Check the URL."));
  }, [tenantSlug]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-amber-50 text-slate-900">
      <IntakeHeader tenant={tenant} />

      <div className="max-w-5xl mx-auto px-6 py-10">
        {!channel && !receipt && (
          <IntroPanel tenant={tenant} onChoose={setChannel} />
        )}

        <AnimatePresence mode="wait">
          {channel && !receipt && (
            <motion.div
              key={channel}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
            >
              <IntakeForm
                tenantSlug={tenantSlug}
                channel={channel}
                allegations={allegations}
                onCancel={() => setChannel(null)}
                onSubmitted={setReceipt}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {receipt && (
          <Receipt receipt={receipt} onViewPortal={() => navigate(`/intake/status?case=${encodeURIComponent(receipt.case_number)}`)} />
        )}
      </div>

      <IntakeFooter tenant={tenant} />
    </div>
  );
}

function IntakeHeader({ tenant }) {
  return (
    <header className="border-b border-slate-200/70 bg-white/70 backdrop-blur">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo variant="dark" size={28} />
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 border-l border-slate-200 pl-3">
            Confidential Intake
          </div>
        </div>
        <Link
          to="/intake/status"
          className="text-sm text-indigo-700 font-medium hover:underline flex items-center gap-1"
          data-testid="intake-header-status-link"
        >
          <Search size={14} strokeWidth={2} /> Track an existing case
        </Link>
      </div>
      {tenant && (
        <div className="max-w-5xl mx-auto px-6 pb-3 text-[11px] font-mono uppercase tracking-widest text-slate-500">
          Workspace: <span className="text-slate-800">{tenant.name}</span>
        </div>
      )}
    </header>
  );
}

function IntroPanel({ tenant, onChoose }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-mono uppercase tracking-widest mb-4">
          <ShieldCheck size={12} strokeWidth={2.5} /> Confidential and audited
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight">
          Report a concern at
          <span className="block text-indigo-800">{tenant?.name || "your workspace"}.</span>
        </h1>
        <p className="mt-4 text-lg text-slate-700 max-w-2xl">
          Your report is reviewed by trained investigators, protected under company policy,
          and audited end to end. Choose the path that feels right for you.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <button
          onClick={() => onChoose("named")}
          className="group text-left rounded-xl border border-slate-200 bg-white hover:border-indigo-500 hover:shadow-lg transition p-6"
          data-testid="intake-choose-named"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-800 grid place-items-center">
              <UserIcon size={18} strokeWidth={2} />
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Named report</span>
          </div>
          <div className="text-xl font-semibold text-slate-900">I want to share who I am</div>
          <p className="mt-2 text-sm text-slate-600">
            Faster clarifying questions. We can email you updates and next steps directly.
          </p>
          <div className="mt-4 text-sm font-medium text-indigo-800 group-hover:underline inline-flex items-center gap-1">
            Continue with named report <ArrowRight size={14} />
          </div>
        </button>

        <button
          onClick={() => onChoose("anonymous")}
          className="group text-left rounded-xl border border-slate-900/10 bg-slate-950 text-slate-100 hover:border-amber-500 hover:shadow-2xl transition p-6"
          data-testid="intake-choose-anonymous"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-400 grid place-items-center">
              <Lock size={18} strokeWidth={2} />
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400">Anonymous report</span>
          </div>
          <div className="text-xl font-semibold">I prefer to stay anonymous</div>
          <p className="mt-2 text-sm text-slate-300">
            Your identity is not stored on the record. You will receive a Case Number and a
            Secure Verification Code to track and add to the case later.
          </p>
          <div className="mt-4 text-sm font-medium text-amber-400 group-hover:underline inline-flex items-center gap-1">
            Continue anonymously <ArrowRight size={14} />
          </div>
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white/60 p-6 grid md:grid-cols-3 gap-6 text-sm">
        <PromiseCard icon={Shield} title="Retaliation prohibited">
          Adverse action against reporters is a violation and is investigated.
        </PromiseCard>
        <PromiseCard icon={Lock} title="Anonymous protection">
          Managers and peers cannot see anonymous reporter identity. Only HR, Company Admin, and Super Admin.
        </PromiseCard>
        <PromiseCard icon={Info} title="Advisory AI">
          PropHR AI helps triage. Final decisions and findings are always made by humans.
        </PromiseCard>
      </div>
    </motion.section>
  );
}

function PromiseCard({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} strokeWidth={2.25} className="text-indigo-700" />
        <div className="font-semibold text-slate-900">{title}</div>
      </div>
      <div className="text-slate-600">{children}</div>
    </div>
  );
}

function IntakeForm({ tenantSlug, channel, allegations, onCancel, onSubmitted }) {
  const isAnon = channel === "anonymous";
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    allegation_tags: [],
    allegation_other_label: "",
    title: "",
    description: "",
    involved_parties: [],
    involved_input: "",
    witnesses: [],
    witness_input: "",
    priority: "medium",
    incident_date: "",
    location: "",
    reporter_name: "",
    reporter_email: "",
    reporter_phone: "",
    reporter_department: "",
    preferred_contact: "email",
    legal_signature: "",
    acknowledged_truthful: false,
  });
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const filteredAlleg = useMemo(() => {
    if (!q.trim()) return allegations;
    const n = q.toLowerCase();
    return allegations.filter((a) =>
      (a.form_label + " " + (a.description || "") + " " + (a.search_keywords || []).join(" "))
        .toLowerCase().includes(n)
    );
  }, [q, allegations]);

  const needsOther = form.allegation_tags.includes("GEN-OTHER");
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (tag) => setField("allegation_tags",
    form.allegation_tags.includes(tag)
      ? form.allegation_tags.filter((t) => t !== tag)
      : [...form.allegation_tags, tag]
  );
  const addChip = (key, inputKey) => {
    const v = form[inputKey].trim();
    if (!v) return;
    setForm((f) => ({ ...f, [key]: [...f[key], v], [inputKey]: "" }));
  };

  const canStep1 = form.allegation_tags.length > 0 && (!needsOther || (form.allegation_other_label.trim().split(/\s+/).length >= 2 && form.allegation_other_label.trim().split(/\s+/).length <= 5));
  const canStep2 = form.description.trim().length >= 20;
  const canStep3 = isAnon
    ? (form.legal_signature.trim().length >= 2 && form.acknowledged_truthful)
    : (form.reporter_name.trim().length >= 2 && /.+@.+\..+/.test(form.reporter_email));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        allegation_tags: form.allegation_tags,
        allegation_other_label: needsOther ? form.allegation_other_label.trim() : null,
        title: form.title.trim() || null,
        description: form.description.trim(),
        involved_parties: form.involved_parties,
        witnesses: form.witnesses,
        priority: form.priority,
        incident_date: form.incident_date || null,
        location: form.location.trim() || null,
      };
      if (isAnon) {
        payload.legal_signature = form.legal_signature.trim();
        payload.acknowledged_truthful = form.acknowledged_truthful;
      } else {
        payload.reporter_name = form.reporter_name.trim();
        payload.reporter_email = form.reporter_email.trim();
        payload.reporter_phone = form.reporter_phone.trim() || null;
        payload.reporter_department = form.reporter_department.trim() || null;
        payload.preferred_contact = form.preferred_contact;
      }
      const url = `${API_BASE}/intake/${tenantSlug}/${isAnon ? "anonymous" : "named"}`;
      const { data } = await axios.post(url, payload);
      onSubmitted(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const containerCls = isAnon
    ? "rounded-xl border border-amber-500/40 bg-slate-950 text-slate-100 shadow-2xl"
    : "rounded-xl border border-slate-200 bg-white shadow-lg";
  const inputCls = isAnon
    ? "w-full bg-slate-900 border border-slate-700 text-slate-100 rounded px-3 py-2 focus:outline-none focus:border-amber-500"
    : "w-full bg-white border border-slate-300 text-slate-900 rounded px-3 py-2 focus:outline-none focus:border-indigo-500";
  const labelCls = "text-[11px] font-mono uppercase tracking-widest " + (isAnon ? "text-slate-400" : "text-slate-500");

  return (
    <div className={containerCls + " p-6 md:p-8"}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className={"text-[10px] font-mono uppercase tracking-widest " + (isAnon ? "text-amber-400" : "text-indigo-700")}>
            {isAnon ? "Anonymous report" : "Named report"} , Step {step} of 3
          </div>
          <h2 className="text-2xl font-semibold mt-1">
            {step === 1 && "What happened?"}
            {step === 2 && "Tell us more"}
            {step === 3 && (isAnon ? "Sign and submit" : "Contact details")}
          </h2>
        </div>
        <button onClick={onCancel} className="text-xs opacity-70 hover:opacity-100 underline" data-testid="intake-cancel">
          Cancel and start over
        </button>
      </div>

      {/* --- STEP 1 --- */}
      {step === 1 && (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Allegation type(s) , select all that apply</label>
            <div className={"mt-2 flex items-center gap-2 " + (isAnon ? "" : "")}>
              <Search size={14} className={isAnon ? "text-slate-500" : "text-slate-400"} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Try 'pay', 'safety', 'threat', 'accommodation'..."
                className={inputCls}
                data-testid="intake-allegation-search"
              />
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
              {filteredAlleg.map((a) => {
                const on = form.allegation_tags.includes(a.system_tag);
                return (
                  <button
                    key={a.system_tag}
                    onClick={() => toggleTag(a.system_tag)}
                    className={"text-left border rounded-lg p-3 transition " +
                      (on
                        ? (isAnon ? "border-amber-500 bg-amber-500/10" : "border-indigo-600 bg-indigo-50")
                        : (isAnon ? "border-slate-800 hover:border-slate-600" : "border-slate-200 hover:border-slate-400"))
                    }
                    data-testid={`intake-alleg-${a.system_tag}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={"w-4 h-4 rounded border grid place-items-center " +
                        (on
                          ? (isAnon ? "bg-amber-500 border-amber-500" : "bg-indigo-600 border-indigo-600")
                          : (isAnon ? "border-slate-600" : "border-slate-400"))}>
                        {on && <CheckCircle2 size={10} className="text-white" strokeWidth={3} />}
                      </div>
                      <div className="text-sm font-semibold">{a.form_label}</div>
                    </div>
                    <div className={"text-xs " + (isAnon ? "text-slate-400" : "text-slate-600")}>
                      {(a.description || "").slice(0, 140)}{(a.description || "").length > 140 ? "..." : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {needsOther && (
            <div>
              <label className={labelCls}>Short label (2 to 5 words) , what is this about?</label>
              <input
                value={form.allegation_other_label}
                onChange={(e) => setField("allegation_other_label", e.target.value)}
                placeholder="e.g., 'unfair overtime approval'"
                className={inputCls}
                data-testid="intake-other-label"
              />
            </div>
          )}

          <div className="flex justify-end">
            <button
              disabled={!canStep1}
              onClick={() => setStep(2)}
              className={"px-5 py-2.5 rounded font-medium " +
                (canStep1
                  ? (isAnon ? "bg-amber-500 text-slate-900 hover:bg-amber-400" : "bg-indigo-700 text-white hover:bg-indigo-800")
                  : "bg-slate-300 text-slate-500 cursor-not-allowed")}
              data-testid="intake-step1-next"
            >
              Continue <ArrowRight size={14} className="inline ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* --- STEP 2 --- */}
      {step === 2 && (
        <div className="space-y-5">
          <div>
            <label className={labelCls}>Short title (optional)</label>
            <input
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="e.g., 'Repeated after-hours texts'"
              className={inputCls}
              data-testid="intake-title"
            />
          </div>

          <div>
            <label className={labelCls}>Describe what happened (minimum 20 characters)</label>
            <textarea
              rows={6}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Include dates, locations, who was involved, and what was said or done. The more concrete detail, the better."
              className={inputCls}
              data-testid="intake-description"
            />
            <div className={"text-[11px] mt-1 " + (isAnon ? "text-slate-500" : "text-slate-500")}>{form.description.trim().length} / 20</div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Priority</label>
              <select className={inputCls} value={form.priority} onChange={(e) => setField("priority", e.target.value)} data-testid="intake-priority">
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Incident date (optional)</label>
              <input type="date" className={inputCls} value={form.incident_date} onChange={(e) => setField("incident_date", e.target.value)} data-testid="intake-date" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Location (optional)</label>
            <input
              value={form.location}
              onChange={(e) => setField("location", e.target.value)}
              placeholder="e.g., '2nd-floor conference room', 'video call', 'Slack #product'"
              className={inputCls}
              data-testid="intake-location"
            />
          </div>

          <ChipInput
            label="Involved parties (optional)"
            placeholder="Add a name and press Enter"
            chips={form.involved_parties}
            inputValue={form.involved_input}
            onInputChange={(v) => setField("involved_input", v)}
            onAdd={() => addChip("involved_parties", "involved_input")}
            onRemove={(i) => setField("involved_parties", form.involved_parties.filter((_, idx) => idx !== i))}
            isAnon={isAnon}
            labelCls={labelCls}
            inputCls={inputCls}
            testid="intake-involved"
          />

          <ChipInput
            label="Witnesses (optional)"
            placeholder="Add a witness name and press Enter"
            chips={form.witnesses}
            inputValue={form.witness_input}
            onInputChange={(v) => setField("witness_input", v)}
            onAdd={() => addChip("witnesses", "witness_input")}
            onRemove={(i) => setField("witnesses", form.witnesses.filter((_, idx) => idx !== i))}
            isAnon={isAnon}
            labelCls={labelCls}
            inputCls={inputCls}
            testid="intake-witness"
          />

          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className={"text-sm opacity-80 hover:opacity-100 " + (isAnon ? "text-slate-300" : "text-slate-700")}>
              Back
            </button>
            <button
              disabled={!canStep2}
              onClick={() => setStep(3)}
              className={"px-5 py-2.5 rounded font-medium " +
                (canStep2
                  ? (isAnon ? "bg-amber-500 text-slate-900 hover:bg-amber-400" : "bg-indigo-700 text-white hover:bg-indigo-800")
                  : "bg-slate-300 text-slate-500 cursor-not-allowed")}
              data-testid="intake-step2-next"
            >
              Continue <ArrowRight size={14} className="inline ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* --- STEP 3 --- */}
      {step === 3 && (
        <div className="space-y-5">
          {isAnon ? (
            <>
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
                <div className="flex items-center gap-2 font-semibold text-amber-400 mb-1">
                  <Shield size={14} /> Anonymous submission
                </div>
                Your name and identifying details will not be stored on the case. Managers and
                peers cannot see who you are. Only HR, Company Admin, and Super Admin can review
                this report. Retaliation for good-faith reports is prohibited.
              </div>
              <div>
                <label className={labelCls}>Typed legal signature (required)</label>
                <input
                  value={form.legal_signature}
                  onChange={(e) => setField("legal_signature", e.target.value)}
                  placeholder="Type your full legal name to certify this report."
                  className={inputCls}
                  data-testid="intake-legal-signature"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Your signature is fingerprinted, not tied to your identity, and used solely
                  for audit integrity.
                </div>
              </div>
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.acknowledged_truthful}
                  onChange={(e) => setField("acknowledged_truthful", e.target.checked)}
                  className="mt-1"
                  data-testid="intake-ack-truthful"
                />
                <span>
                  I certify that this report is truthful to the best of my knowledge and that
                  I understand knowingly false reports may result in disciplinary action.
                </span>
              </label>
            </>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Full name</label>
                  <input value={form.reporter_name} onChange={(e) => setField("reporter_name", e.target.value)} className={inputCls} data-testid="intake-name" />
                </div>
                <div>
                  <label className={labelCls}>Work email</label>
                  <input type="email" value={form.reporter_email} onChange={(e) => setField("reporter_email", e.target.value)} className={inputCls} data-testid="intake-email" />
                </div>
                <div>
                  <label className={labelCls}>Phone (optional)</label>
                  <input value={form.reporter_phone} onChange={(e) => setField("reporter_phone", e.target.value)} className={inputCls} data-testid="intake-phone" />
                </div>
                <div>
                  <label className={labelCls}>Department (optional)</label>
                  <input value={form.reporter_department} onChange={(e) => setField("reporter_department", e.target.value)} className={inputCls} data-testid="intake-department" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Preferred contact method</label>
                <div className="mt-2 flex gap-3 text-sm">
                  {["email", "phone", "either"].map((v) => (
                    <label key={v} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="pref-contact"
                        checked={form.preferred_contact === v}
                        onChange={() => setField("preferred_contact", v)}
                        data-testid={`intake-contact-${v}`}
                      />
                      <span className="capitalize">{v}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(2)} className={"text-sm opacity-80 hover:opacity-100 " + (isAnon ? "text-slate-300" : "text-slate-700")}>
              Back
            </button>
            <button
              disabled={!canStep3 || submitting}
              onClick={submit}
              className={"px-6 py-2.5 rounded font-medium " +
                ((canStep3 && !submitting)
                  ? (isAnon ? "bg-amber-500 text-slate-900 hover:bg-amber-400" : "bg-indigo-700 text-white hover:bg-indigo-800")
                  : "bg-slate-300 text-slate-500 cursor-not-allowed")}
              data-testid="intake-submit"
            >
              {submitting ? "Submitting..." : "Submit report"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChipInput({ label, placeholder, chips, inputValue, onInputChange, onAdd, onRemove, isAnon, labelCls, inputCls, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2 mt-2">
        <input
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
          placeholder={placeholder}
          className={inputCls + " flex-1"}
          data-testid={`${testid}-input`}
        />
        <button
          onClick={onAdd}
          className={"px-3 rounded text-sm font-medium " +
            (isAnon ? "bg-slate-800 text-slate-100 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200")}
          data-testid={`${testid}-add`}
        >
          Add
        </button>
      </div>
      {chips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {chips.map((c, i) => (
            <span key={i} className={"text-xs rounded-full px-3 py-1 " +
              (isAnon ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-800")}>
              {c}
              <button onClick={() => onRemove(i)} className="ml-2 opacity-60 hover:opacity-100">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Receipt({ receipt, onViewPortal }) {
  const isAnon = receipt.channel === "anonymous";
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const text = `Case Number: ${receipt.case_number}\nVerification Code: ${receipt.verification_code}`;
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed. Please save these details manually."); }
  };
  return (
    <motion.section
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-xl border-2 border-emerald-500 bg-emerald-50 p-6 md:p-8"
      data-testid="intake-receipt"
    >
      <div className="flex items-center gap-2 text-emerald-700 mb-3">
        <CheckCircle2 size={20} strokeWidth={2.25} />
        <div className="font-semibold">Report submitted. Thank you for speaking up.</div>
      </div>
      <div className="text-slate-800 mb-5">
        {isAnon
          ? "Save your Case Number and Secure Verification Code. This is the only way to check your case status or add to it later. PropHR cannot recover them."
          : "You will receive email confirmation at your work address. Bookmark the portal link below for quick access."}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-emerald-500/40 bg-white p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Case Number</div>
          <div className="mt-1 text-2xl font-mono font-semibold text-slate-900" data-testid="receipt-case-number">
            {receipt.case_number}
          </div>
        </div>
        <div className="rounded-lg border border-emerald-500/40 bg-white p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Secure Verification Code</div>
          <div className="mt-1 text-2xl font-mono font-semibold text-slate-900" data-testid="receipt-verification-code">
            {receipt.verification_code}
          </div>
        </div>
      </div>

      {receipt.warning && (
        <div className="mt-5 rounded border border-amber-500/50 bg-amber-50 text-amber-900 text-sm p-3">
          <strong className="font-semibold">Important: </strong>{receipt.warning}
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={copy} className="px-4 py-2 rounded bg-slate-900 text-white text-sm inline-flex items-center gap-2" data-testid="receipt-copy">
          <Copy size={14} /> {copied ? "Copied" : "Copy credentials"}
        </button>
        <button onClick={onViewPortal} className="px-4 py-2 rounded bg-indigo-700 text-white text-sm inline-flex items-center gap-2" data-testid="receipt-view-portal">
          <MessageSquare size={14} /> Open case status portal
        </button>
      </div>
    </motion.section>
  );
}

function IntakeFooter({ tenant }) {
  return (
    <footer className="border-t border-slate-200 mt-16 bg-white/70">
      <div className="max-w-5xl mx-auto px-6 py-6 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <HelpCircle size={12} /> Confidential and audited under {tenant?.name || "your workspace"} policies (GDPR, CCPA, EEOC, SOC 2).
        </div>
        <div className="font-mono">© {new Date().getFullYear()} PropHR</div>
      </div>
    </footer>
  );
}
